import { handleError, hasError, tryCatch, unknownError } from '@repo/shared/api/helpers'
import {
  OutputTypeForBENEFICIARY_PROFILE_FILE_LOAD,
  OutputTypeForFILE_IMPORT,
  OutputTypeForFILE_SUMMARY_SUGGEST,
  PromptTemplateType,
} from '@repo/shared/dtos/organization'
import { StructuredDataValueDTO } from '@repo/shared/dtos/structured-data'
import { BeneficiariesRoutes } from '@repo/shared/routes-beneficiaries'
import { DocumentsRoutes } from '@repo/shared/routes-documents'
import { FilesRoutes } from '@repo/shared/routes-files'
import { PublicRoutes } from '@repo/shared/routes-public'
import { AxiosInstance } from 'axios'

import { buildBeneficiaryFromDto, buildDocumentFromDto } from '@/external/api/helpers'
import { Document } from '@/store/document'
import { mockedData } from '@/store/mock/mock'
import { IApi } from '@/store/spi/api'

type T = {
  app: IApi['beneficiaries']
}

export const apiBeneficiaries: (api?: AxiosInstance) => T = (api): T => ({
  //
  // APP
  //
  app: {
    one: {
      askAbout: api
        ? async (params) => {
            const res = await tryCatch(
              async () =>
                await api.post<BeneficiariesRoutes.one.askAbout.Response>(
                  BeneficiariesRoutes.one.askAbout.call({
                    organizationId: params.organizationId,
                    beneficiaryId: params.beneficiaryId,
                  }),
                  { params } satisfies BeneficiariesRoutes.one.askAbout.Request,
                ),
            )
            if (hasError(res)) return handleError(res)
            return res
          }
        : () => {
            return Promise.resolve({
              output: 'This is an answer.',
            })
          },

      refreshProfile: api
        ? async (params) => {
            const res = await tryCatch(
              async () =>
                await api.post<BeneficiariesRoutes.one.refreshProfile.Response>(
                  BeneficiariesRoutes.one.refreshProfile.call({
                    organizationId: params.organizationId,
                    beneficiaryId: params.beneficiaryId,
                  }),
                  {
                    params,
                  } satisfies BeneficiariesRoutes.one.refreshProfile.Request,
                ),
            )
            if (hasError(res)) return handleError(res)
            return res
          }
        : () => {
            return Promise.resolve({
              profile: 'this is a beneficary profile',
            })
          },

      refreshStructuredData: api
        ? async (params) => {
            const res = await tryCatch(
              async () =>
                await api.post<BeneficiariesRoutes.one.refreshStructuredData.Response>(
                  BeneficiariesRoutes.one.refreshStructuredData.call({
                    organizationId: params.organizationId,
                    beneficiaryId: params.beneficiaryId,
                  }),
                  {
                    params,
                  } satisfies BeneficiariesRoutes.one.refreshStructuredData.Request,
                ),
            )
            if (hasError(res)) return handleError(res)
            return res
          }
        : () => {
            return Promise.resolve({
              structuredData: [
                {
                  schemaItemId: 'id-1',
                  value: 'James Bond',
                },
                {
                  schemaItemId: 'id-2',
                  value: '37',
                },
              ] satisfies StructuredDataValueDTO,
            })
          },

      create: api
        ? async ({ beneficiaryFields, organizationId }) => {
            const res = await tryCatch(
              async () =>
                await api.post<BeneficiariesRoutes.one.create.Response>(
                  BeneficiariesRoutes.one.create.call({ organizationId }),
                  {
                    beneficiaryFields,
                  } satisfies BeneficiariesRoutes.one.create.Request,
                ),
            )
            if (hasError(res)) return handleError(res)
            return buildBeneficiaryFromDto(res.beneficiary)
          }
        : (p) => {
            if (p.beneficiaryFields.fullName) {
              return Promise.resolve({
                ...mockedData.beneficiary,
                fullName: p.beneficiaryFields.fullName,
                profile: p.beneficiaryFields.profile,
              })
            }
            return Promise.resolve(unknownError)
          },

      patch: api
        ? async ({ beneficiaryId, organizationId, beneficiaryFields }) => {
            const res = await tryCatch(
              async () =>
                await api.patch<BeneficiariesRoutes.one.patch.Response>(
                  BeneficiariesRoutes.one.patch.call({
                    organizationId,
                    beneficiaryId,
                  }),
                  {
                    beneficiaryFields,
                  } satisfies BeneficiariesRoutes.one.patch.Request,
                ),
            )
            if (hasError(res)) return handleError(res)
            return buildBeneficiaryFromDto(res.beneficiary)
          }
        : (p) => {
            if (p.beneficiaryFields.fullName) {
              return Promise.resolve({
                ...mockedData.beneficiaries[0]!,
                fullName: p.beneficiaryFields.fullName,
              })
            }
            return Promise.resolve(unknownError)
          },
      processProfile: api
        ? async ({ organizationId, file }) => {
            const formData = new FormData()
            formData.append('file', file)
            const res = await tryCatch(
              async () =>
                await api.post<FilesRoutes.processFile.Response>(
                  FilesRoutes.processFile.call({
                    organizationId,
                    processFilePromptTemplateType: PromptTemplateType.BENEFICIARY_PROFILE_FILE_LOAD,
                  }),
                  formData,
                  {
                    headers: {
                      'Content-Type': 'multipart/form-data',
                    },
                  },
                ),
            )
            if (hasError(res)) return handleError(res)
            return res.promptResult as OutputTypeForBENEFICIARY_PROFILE_FILE_LOAD
          }
        : () => {
            return Promise.resolve({
              lang: 'eng',
              fullName: 'full name',
              profile: 'profile content',
              traceUrl: '',
            } as OutputTypeForBENEFICIARY_PROFILE_FILE_LOAD)
          },
      processNote: api
        ? async ({ organizationId, file, instructions }) => {
            const formData = new FormData()
            formData.append('file', file)
            if (instructions) formData.append('instructions', instructions)
            const res = await tryCatch(
              async () =>
                await api.post<FilesRoutes.processFile.Response>(
                  FilesRoutes.processFile.call({
                    organizationId,
                    processFilePromptTemplateType: instructions
                      ? PromptTemplateType.FILE_IMPORT
                      : PromptTemplateType.FILE_SUMMARY_SUGGEST,
                  }),
                  formData,
                  {
                    headers: {
                      'Content-Type': 'multipart/form-data',
                    },
                  },
                ),
            )
            if (hasError(res)) return handleError(res)
            return instructions
              ? (res.promptResult as OutputTypeForFILE_IMPORT)
              : (res.promptResult as OutputTypeForFILE_SUMMARY_SUGGEST)
          }
        : () => {
            return Promise.resolve({
              lang: 'eng',
              summary: 'processed file content',
              traceUrl: '',
            } as OutputTypeForFILE_SUMMARY_SUGGEST)
          },
      generateReport: api
        ? async (params) => {
            const res = await tryCatch(
              async () =>
                await api.post<BeneficiariesRoutes.one.generateReport.Response>(
                  BeneficiariesRoutes.one.generateReport.call({
                    organizationId: params.organizationId,
                    beneficiaryId: params.beneficiaryId,
                  }),
                  {
                    params,
                  } satisfies BeneficiariesRoutes.one.generateReport.Request,
                ),
            )
            if (hasError(res)) return handleError(res)
            return res
          }
        : () => {
            return Promise.resolve({ report: 'this is a report' })
          },

      get: api
        ? async (p) => {
            const res = await tryCatch(
              async () =>
                await api.get<BeneficiariesRoutes.one.get.Response>(
                  BeneficiariesRoutes.one.get.call(p),
                ),
            )
            if (hasError(res)) return handleError(res)
            return buildBeneficiaryFromDto(res.beneficiary)
          }
        : () => {
            return Promise.resolve(mockedData.beneficiary)
          },

      getInitialValues: api
        ? async ({ beneficiaryId, organizationId }) => {
            const chatSessionsRes = await tryCatch(
              async () =>
                await api.get<PublicRoutes.beneficiaries.getChatSessionsFromOne.Response>(
                  PublicRoutes.beneficiaries.getChatSessionsFromOne.call({
                    beneficiaryId,
                  }),
                ),
            )
            if (hasError(chatSessionsRes)) return handleError(chatSessionsRes)

            const documentsRes = await tryCatch(
              async () =>
                await api.get<DocumentsRoutes.get.Response>(
                  DocumentsRoutes.get.call({ beneficiaryId, organizationId }),
                ),
            )
            if (hasError(documentsRes)) return handleError(documentsRes)

            return {
              chatSessions: chatSessionsRes.chatSessions,
              documents: documentsRes.documents.map(buildDocumentFromDto),
            }
          }
        : () => {
            return Promise.resolve({
              chatSessions: mockedData.chatSessions,
              documents: mockedData.notes as Document[],
              tasks: mockedData.tasks,
            })
          },
    },

    askAbout: api
      ? async (params) => {
          const res = await tryCatch(
            async () =>
              await api.post<BeneficiariesRoutes.askAbout.Response>(
                BeneficiariesRoutes.one.askAbout.call({
                  organizationId: params.organizationId,
                }),
                { params } satisfies BeneficiariesRoutes.askAbout.Request,
              ),
          )
          if (hasError(res)) return handleError(res)
          return res
        }
      : () => {
          return Promise.resolve({
            lang: 'eng',
            items: [],
          })
        },

    get: api
      ? async ({ organizationId, asCaseWorkerId }) => {
          const res = await tryCatch(
            async () =>
              await api.get<BeneficiariesRoutes.get.Response>(
                BeneficiariesRoutes.get.call({
                  asCaseWorkerId,
                  organizationId,
                }),
              ),
          )
          if (hasError(res)) return handleError(res)
          return res.beneficiaries.map(buildBeneficiaryFromDto)
        }
      : () => {
          const beneficiaries = mockedData.beneficiaries
          return Promise.resolve(beneficiaries)
        },
  },
})
